import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaperPlaneTilt, X, FloppyDisk, Warning, Users, Code, Eye, Paperclip, File as FileIcon } from "@phosphor-icons/react";

const MAX_TOTAL_BYTES = 15 * 1024 * 1024; // 15MB total, Resend limit ~40MB but keep safe
const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

export default function Compose() {
  const [recipientInput, setRecipientInput] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);
  const [attachments, setAttachments] = useState([]); // {filename, content(base64), size}
  const [configured, setConfigured] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tmplName, setTmplName] = useState("");

  useEffect(() => {
    api.get("/config-status").then((r) => setConfigured(r.data.configured)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const isEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const addRecipients = (raw) => {
    const parts = raw.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);
    const valid = [];
    parts.forEach((p) => {
      if (!isEmail(p)) {
        toast.error(`Invalid email: ${p}`);
      } else if (recipients.includes(p) || valid.includes(p)) {
        // skip dupes
      } else {
        valid.push(p);
      }
    });
    if (valid.length) setRecipients((prev) => [...prev, ...valid]);
    setRecipientInput("");
  };

  const handleKeyDown = (e) => {
    if (["Enter", ",", " ", ";"].includes(e.key)) {
      e.preventDefault();
      if (recipientInput.trim()) addRecipients(recipientInput);
    } else if (e.key === "Backspace" && !recipientInput && recipients.length) {
      setRecipients((prev) => prev.slice(0, -1));
    }
  };

  const removeRecipient = (email) =>
    setRecipients((prev) => prev.filter((r) => r !== email));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    const readFile = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]); // strip data: prefix
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

    let running = attachments.reduce((s, a) => s + a.size, 0);
    const next = [...attachments];
    for (const file of files) {
      if (next.some((a) => a.filename === file.name && a.size === file.size)) continue;
      if (running + file.size > MAX_TOTAL_BYTES) {
        toast.error(`"${file.name}" skipped — total attachments exceed 15 MB`);
        continue;
      }
      try {
        const content = await readFile(file);
        next.push({ filename: file.name, content, size: file.size });
        running += file.size;
      } catch {
        toast.error(`Failed to read "${file.name}"`);
      }
    }
    setAttachments(next);
  };

  const removeAttachment = (idx) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (t) {
      setSubject(t.subject);
      setBody(t.html_content);
      toast.success(`Loaded template “${t.name}”`);
    }
  };

  const send = async () => {
    const all = [...recipients];
    if (recipientInput.trim() && isEmail(recipientInput.trim())) all.push(recipientInput.trim());
    if (!all.length) return toast.error("Add at least one recipient");
    if (!subject.trim()) return toast.error("Subject is required");
    if (!body.trim()) return toast.error("Message body is required");

    setSending(true);
    try {
      const res = await api.post("/send-email", {
        recipients: all,
        subject,
        html_content: body,
        attachments: attachments.map(({ filename, content }) => ({ filename, content })),
      });
      const { sent_count, failed_count, status } = res.data;
      if (status === "partial") {
        toast.warning(`Sent to ${sent_count}, ${failed_count} failed`);
      } else {
        toast.success(
          sent_count > 1 ? `Sent to ${sent_count} recipients` : "Email sent successfully"
        );
      }
      setRecipients([]);
      setRecipientInput("");
      setSubject("");
      setBody("");
      setAttachments([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const saveTemplate = async () => {
    if (!tmplName.trim()) return toast.error("Template name is required");
    try {
      const res = await api.post("/templates", {
        name: tmplName,
        subject,
        html_content: body,
      });
      setTemplates((prev) => [res.data, ...prev]);
      setSaveOpen(false);
      setTmplName("");
      toast.success("Template saved");
    } catch {
      toast.error("Failed to save template");
    }
  };

  const totalRecipients = recipients.length + (recipientInput.trim() && isEmail(recipientInput.trim()) ? 1 : 0);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 md:py-14 md:px-12">
      <header className="mb-8">
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Compose</h1>
        <p className="text-muted-foreground mt-2">
          Write once, deliver to one person or a whole list — all over HTTP.
        </p>
      </header>

      {!configured && (
        <div
          data-testid="config-warning"
          className="mb-8 flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3"
        >
          <Warning size={20} className="text-destructive mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-destructive">Resend API key not set</p>
            <p className="text-muted-foreground mt-0.5">
              Add your <span className="font-mono">RESEND_API_KEY</span> in{" "}
              <span className="font-mono">backend/.env</span> to start sending. Get one free at{" "}
              <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline text-primary">
                resend.com
              </a>.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Recipients</Label>
            {totalRecipients > 0 && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono" data-testid="recipient-count">
                <Users size={14} /> {totalRecipients}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 rounded-md border border-input bg-card p-2 focus-within:border-primary transition-colors duration-200">
            {recipients.map((r) => (
              <span
                key={r}
                data-testid={`recipient-chip-${r}`}
                className="flex items-center gap-1.5 bg-secondary text-secondary-foreground text-xs font-mono px-2.5 py-1 rounded"
              >
                {r}
                <button onClick={() => removeRecipient(r)} data-testid={`remove-recipient-${r}`} className="hover:text-destructive transition-colors">
                  <X size={13} />
                </button>
              </span>
            ))}
            <input
              data-testid="recipient-input"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => recipientInput.trim() && addRecipients(recipientInput)}
              placeholder={recipients.length ? "Add another…" : "email@example.com  (comma or Enter to add multiple)"}
              className="flex-1 min-w-[200px] bg-transparent outline-none text-sm py-1 px-1 font-mono"
            />
          </div>
        </div>

        {templates.length > 0 && (
          <div>
            <Label className="text-sm font-semibold mb-2 block">Start from template</Label>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger data-testid="template-select" className="bg-card">
                <SelectValue placeholder="Choose a saved template…" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label className="text-sm font-semibold mb-2 block">Subject</Label>
          <Input
            data-testid="subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="What's this about?"
            className="bg-card font-display text-lg h-12"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Message (HTML)</Label>
            <div className="flex items-center gap-1 rounded-full bg-secondary p-0.5">
              <button
                type="button"
                data-testid="tab-write"
                onClick={() => setPreview(false)}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full transition-colors duration-200 ${
                  !preview ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
                }`}
              >
                <Code size={14} /> Write
              </button>
              <button
                type="button"
                data-testid="tab-preview"
                onClick={() => setPreview(true)}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full transition-colors duration-200 ${
                  preview ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
                }`}
              >
                <Eye size={14} /> Preview
              </button>
            </div>
          </div>
          {preview ? (
            <div
              data-testid="body-preview"
              className="email-preview bg-card border border-input rounded-md min-h-[280px] p-4 overflow-auto text-sm"
              dangerouslySetInnerHTML={{
                __html: body.trim() ? body : '<p style="color:#7A7A75">Nothing to preview yet…</p>',
              }}
            />
          ) : (
            <Textarea
              data-testid="body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="<h1>Hello 👋</h1>\n<p>Write raw HTML here — it's sent exactly as you write it.</p>"
              className="bg-card min-h-[280px] resize-y leading-relaxed font-mono text-sm"
            />
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Your HTML is sent as-is. Use inline styles for best email-client support.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Attachments</Label>
            <label
              data-testid="attach-btn"
              className="flex items-center gap-1.5 text-xs font-medium text-primary cursor-pointer hover:opacity-80 transition-opacity"
            >
              <Paperclip size={15} /> Add files
              <input
                data-testid="attach-input"
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          {attachments.length === 0 ? (
            <p className="text-xs text-muted-foreground">No files attached. Up to 15 MB total.</p>
          ) : (
            <div className="space-y-2" data-testid="attachment-list">
              {attachments.map((a, i) => (
                <div
                  key={`${a.filename}-${i}`}
                  data-testid={`attachment-${i}`}
                  className="flex items-center gap-3 border border-border rounded-md bg-card px-3 py-2"
                >
                  <FileIcon size={18} className="text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{a.filename}</p>
                    <p className="text-xs text-muted-foreground font-mono">{fmtSize(a.size)}</p>
                  </div>
                  <button
                    data-testid={`remove-attachment-${i}`}
                    onClick={() => removeAttachment(i)}
                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            data-testid="save-template-btn"
            onClick={() => {
              if (!subject.trim() && !body.trim()) return toast.error("Nothing to save yet");
              setSaveOpen(true);
            }}
            className="rounded-full"
          >
            <FloppyDisk size={17} className="mr-2" /> Save as template
          </Button>
          <Button
            data-testid="send-email-btn"
            onClick={send}
            disabled={sending}
            className="rounded-full bg-primary hover:bg-primary/90 transition-transform duration-200 hover:-translate-y-0.5"
          >
            <PaperPlaneTilt size={17} weight="fill" className="mr-2" />
            {sending ? "Sending…" : totalRecipients > 1 ? `Send to ${totalRecipients}` : "Send email"}
          </Button>
        </div>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Save template</DialogTitle>
            <DialogDescription>Give this email a name to reuse it later.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-sm font-semibold mb-2 block">Template name</Label>
            <Input
              data-testid="template-name-input"
              value={tmplName}
              onChange={(e) => setTmplName(e.target.value)}
              placeholder="e.g. Weekly newsletter"
              className="bg-background"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)} data-testid="cancel-save-template">
              Cancel
            </Button>
            <Button onClick={saveTemplate} data-testid="confirm-save-template" className="rounded-full">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
